import { ChevronLeft, ChevronRight } from "lucide-react";
import { Minus, Plus, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";


export const ModuleReaderPageImage = ({ imageUrl, moduleTitle, currentPage, loading, zoomLevel, zoomMode, focusMode, onPrevious, onNext, previousDisabled, nextDisabled, pageCount, onExitFocusMode, onZoomIn, onZoomOut, canZoomIn, canZoomOut }) => {
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollTop = 0;
  }, [currentPage, focusMode, imageUrl]);

  return (
    <div className={`${focusMode ? "relative flex-1 overflow-hidden bg-[#e7e0d3] p-0" : "relative rounded-[1.5rem] border border-stone-200 bg-white p-2 shadow-[0_24px_55px_rgba(31,45,61,0.08)] sm:p-3"}`} data-testid="module-reader-page-frame">
      {loading ? (
        <div className="flex min-h-[28rem] items-center justify-center rounded-[1.2rem] bg-[#fcfaf5] px-6 text-center text-sm text-slate-600" data-testid="module-reader-page-loading-state">
          Loading page {currentPage}...
        </div>
      ) : (
        <>
          {focusMode ? (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-start justify-between px-3 pt-3 sm:px-5 sm:pt-5" data-testid="module-reader-focus-overlay-controls">
              <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-stone-200/80 bg-white/90 p-1 shadow-lg backdrop-blur">
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-full text-slate-700 hover:bg-stone-100" onClick={onZoomOut} disabled={!canZoomOut} data-testid="module-reader-focus-zoom-out-button">
                  <Minus className="h-4 w-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-full text-slate-700 hover:bg-stone-100" onClick={onZoomIn} disabled={!canZoomIn} data-testid="module-reader-focus-zoom-in-button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <Button type="button" size="icon" variant="ghost" className="pointer-events-auto h-10 w-10 rounded-full border border-stone-200/80 bg-white/90 text-slate-700 shadow-lg backdrop-blur hover:bg-stone-100" onClick={onExitFocusMode} data-testid="module-reader-focus-exit-button">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : null}

          <div ref={scrollContainerRef} className={`${focusMode ? "h-full overflow-auto px-2 pb-20 pt-16 sm:px-12 sm:pb-10 sm:pt-6" : "max-h-[78vh] overflow-auto rounded-[1.2rem] border border-stone-200 bg-[#f8f6f1] p-1 sm:max-h-[80vh] sm:p-2"}`} data-testid="module-reader-page-scroll-container">
            <div className={`relative flex min-h-full items-start justify-center ${focusMode ? "px-0" : "px-0 sm:px-3"}`}>
              <img
                src={imageUrl}
                alt={`${moduleTitle} page ${currentPage}`}
                className="h-auto max-w-none rounded-[1.1rem] border border-stone-200 bg-white object-contain shadow-[0_20px_45px_rgba(31,45,61,0.12)]"
                style={zoomMode === "fit" ? { width: "100%", minWidth: "100%" } : { width: `${zoomLevel}%`, minWidth: `${zoomLevel}%` }}
                data-testid="module-reader-page-image"
              />
            </div>
          </div>

          {focusMode ? (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-3 z-20 hidden items-center md:flex" data-testid="module-reader-focus-left-rail">
                <Button type="button" variant="outline" className="pointer-events-auto h-12 w-12 rounded-full border-[#d7c6a9] bg-white/92 shadow-lg backdrop-blur" onClick={onPrevious} disabled={previousDisabled} data-testid="module-reader-floating-previous-button">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </div>
              <div className="pointer-events-none absolute inset-y-0 right-3 z-20 hidden items-center md:flex" data-testid="module-reader-focus-right-rail">
                <Button type="button" variant="outline" className="pointer-events-auto h-12 w-12 rounded-full border-[#d7c6a9] bg-white/92 shadow-lg backdrop-blur" onClick={onNext} disabled={nextDisabled} data-testid="module-reader-floating-next-button">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-3 md:hidden" data-testid="module-reader-mobile-focus-pager">
                <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-stone-200/80 bg-white/92 px-2 py-1.5 shadow-lg backdrop-blur">
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-full text-slate-700 hover:bg-stone-100" onClick={onPrevious} disabled={previousDisabled} data-testid="module-reader-mobile-previous-button">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500" data-testid="module-reader-mobile-page-indicator">Page {currentPage} of {pageCount}</span>
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-full text-slate-700 hover:bg-stone-100" onClick={onNext} disabled={nextDisabled} data-testid="module-reader-mobile-next-button">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="sticky bottom-2 mt-3 flex items-center justify-between gap-3 rounded-full border border-stone-200 bg-white/95 px-3 py-2 shadow-lg md:hidden" data-testid="module-reader-mobile-pager-bar">
              <Button type="button" variant="outline" className="rounded-full border-[#d7c6a9]" onClick={onPrevious} disabled={previousDisabled} data-testid="module-reader-mobile-previous-button">
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" data-testid="module-reader-mobile-page-indicator">Page {currentPage} of {pageCount}</span>
              <Button type="button" className="rounded-full bg-[#1f2d3d] hover:bg-[#15212d]" onClick={onNext} disabled={nextDisabled} data-testid="module-reader-mobile-next-button">
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
